import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useLanguage } from '../contexts/LanguageContext';
import { FormTemplate } from '../types/formBuilder';
import { TemplateService } from '../services/templateService';
import { FormSubmissionService } from '../services/formSubmissionService';
import FormRenderer from '../components/forms/FormRenderer';
import LoadingScreen from '../components/common/LoadingScreen';
import {
  DocumentTextIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  SunIcon,
  MoonIcon,
  LanguageIcon
} from '@heroicons/react/24/outline';
import YouCRMLogo from '../assets/youcrm.svg';

const PublicFormFiller: React.FC = () => {
  const { publicId } = useParams<{ publicId: string }>();
  const navigate = useNavigate();
  const { t, language, setLanguage } = useLanguage();

  const [template, setTemplate] = useState<FormTemplate | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    if (publicId) {
      loadPublicTemplate(publicId);
    } else {
      setError('Invalid form link');
      setIsLoading(false);
    }
  }, [publicId]);

  const loadPublicTemplate = async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const publicTemplate = await TemplateService.getPublicFormTemplate(id);

      if (!publicTemplate) {
        setError('Form not found or no longer available');
        return;
      }

      // Check if form is still accepting submissions
      if (publicTemplate.publicSettings?.maxSubmissions) {
        const currentSubmissions = publicTemplate.publicSettings.currentSubmissions || 0;
        if (currentSubmissions >= publicTemplate.publicSettings.maxSubmissions) {
          setError('This form is no longer accepting submissions');
          return;
        }
      }

      // Check if form has expired
      if (publicTemplate.publicSettings?.expiresAt && 
          new Date() > new Date(publicTemplate.publicSettings.expiresAt)) {
        setError('This form has expired');
        return;
      }

      setTemplate(publicTemplate);
    } catch (error) {
      console.error('Error loading public template:', error);
      setError('Failed to load form. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormSubmit = async (formData: Record<string, any>) => {
    if (!template) return;

    setIsSubmitting(true);
    try {
      // Create submission data
      const submissionData = {
        templateId: template.id,
        templateName: template.name,
        organizationId: template.organizationId,
        data: formData,
        submittedAt: new Date(),
        submittedBy: 'anonymous', // For public forms
        submittedByName: 'Anonymous User',
        status: 'submitted' as const,
        isPublic: true
      };

      await FormSubmissionService.createSubmission(submissionData);

      // Update submission count if there's a limit
      if (template.publicSettings?.maxSubmissions) {
        const currentSubmissions = (template.publicSettings.currentSubmissions || 0) + 1;
        await TemplateService.updateFormTemplate(template.id, {
          publicSettings: {
            ...template.publicSettings,
            currentSubmissions
          }
        });
      }

      setIsSubmitted(true);
      toast.success(t('formBuilder.publicForm.submitSuccess') || 'Form submitted successfully!');
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(t('formFiller.submitError') || 'Failed to submit form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    // For public forms, we might want to redirect to a landing page
    // or just show a message
    window.close();
  };

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    if (!isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const toggleLanguage = () => {
    const newLanguage = language === 'hu' ? 'en' : 'hu';
    setLanguage(newLanguage);
  };

  // Initialize dark mode on component mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark') {
      setIsDarkMode(true);
      document.documentElement.classList.add('dark');
    }
  }, []);

  // Save theme preference
  useEffect(() => {
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl shadow-xl border border-red-100 dark:border-gray-700 p-8 text-center">
          {/* Error Icon */}
          <div className="w-20 h-20 bg-gradient-to-r from-red-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
            <ExclamationTriangleIcon className="h-10 w-10 text-white" />
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
            {t('formBuilder.publicForm.error') || 'Form Unavailable'}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
            {error}
          </p>
          
          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={() => window.close()}
              className="w-full px-4 py-3 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white rounded-lg transition-all duration-200 font-medium shadow-lg"
            >
              {t('common.close') || 'Close'}
            </button>
            <button
              onClick={() => window.location.reload()}
              className="w-full px-4 py-3 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors font-medium"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl shadow-xl border border-green-100 dark:border-gray-700 p-8 text-center">
          {/* Success Icon with Animation */}
          <div className="relative mx-auto mb-6 w-20 h-20">
            <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center shadow-lg">
              <CheckCircleIcon className="h-10 w-10 text-white" />
            </div>
            {/* Success Ring Animation - csak a kör körül */}
            <div className="absolute inset-0 rounded-full border-2 border-green-400 animate-ping opacity-75"></div>
          </div>
          
          <h2 className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-3">
            {t('formBuilder.publicForm.submitSuccess') || 'Form Submitted Successfully!'}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
            {t('formBuilder.publicForm.submitSuccessMessage') || 'Thank you for your submission. Your response has been recorded.'}
          </p>
          
          {/* Success Features */}
          <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-center space-x-4 text-sm text-green-700 dark:text-green-300">
              <div className="flex items-center space-x-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Saved Securely</span>
              </div>
              <div className="flex items-center space-x-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <span>Confirmation Sent</span>
              </div>
            </div>
          </div>
          
          <button
            onClick={() => {
              setIsSubmitted(false);
              if (publicId) {
                loadPublicTemplate(publicId);
              }
            }}
            className="w-full px-4 py-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-lg transition-all duration-200 font-medium shadow-lg"
          >
            {t('common.close') || 'Bezárás'}
          </button>
        </div>
      </div>
    );
  }

  if (!template) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-100 via-purple-50 to-pink-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex flex-col">
      {/* Modern Header */}
      <div className="bg-white/75 dark:bg-gray-800/75 backdrop-blur-lg shadow-lg border-b border-indigo-200/50 dark:border-gray-600/30 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* YouCRM Logo */}
            <img 
              src={YouCRMLogo} 
              alt="YouCRM" 
              className="h-8 w-auto filter drop-shadow-lg"
            />

            {/* Controls */}
            <div className="flex items-center space-x-3">
              {/* Language Toggle */}
              <button
                onClick={toggleLanguage}
                className="flex items-center space-x-1 px-3 py-2 bg-white/20 dark:bg-gray-700/50 backdrop-blur-sm text-gray-700 dark:text-gray-300 rounded-lg hover:bg-white/30 dark:hover:bg-gray-600/50 transition-colors border border-white/30 dark:border-gray-600/30"
                title={language === 'hu' ? 'Switch to English' : 'Váltás magyarra'}
              >
                <LanguageIcon className="h-4 w-4" />
                <span className="text-sm font-medium">{language.toUpperCase()}</span>
              </button>

              {/* Theme Toggle */}
              <button
                onClick={toggleDarkMode}
                className="p-2 bg-white/20 dark:bg-gray-700/50 backdrop-blur-sm text-gray-700 dark:text-gray-300 rounded-lg hover:bg-white/30 dark:hover:bg-gray-600/50 transition-colors border border-white/30 dark:border-gray-600/30"
                title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              >
                {isDarkMode ? (
                  <SunIcon className="h-4 w-4" />
                ) : (
                  <MoonIcon className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="flex-1 max-w-4xl mx-auto px-4 py-8 w-full">
        {/* Form Card with modern styling */}
        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-lg rounded-2xl shadow-2xl border border-indigo-200/50 dark:border-gray-600/30 overflow-hidden">
          {/* Form Renderer */}
          <div className="pt-6">
            <FormRenderer
              template={template}
              onSubmit={handleFormSubmit}
              onCancel={handleCancel}
              isSubmitting={isSubmitting}
              showCancelButton={false} // Don't show cancel for public forms
              className="p-0" // Remove default padding since we handle it in the card
            />
          </div>
        </div>
        
        {/* Trust indicators */}
        <div className="mt-6 flex items-center justify-center space-x-6 text-xs text-gray-500 dark:text-gray-400">
          <div className="flex items-center space-x-1">
            <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span>Secure & Encrypted</span>
          </div>
          <div className="flex items-center space-x-1">
            <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 8a6 6 0 01-7.743 5.743L10 14l-.257-.257A6 6 0 1118 8zm-2 0a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
            </svg>
            <span>GDPR Compliant</span>
          </div>
          <div className="flex items-center space-x-1">
            <svg className="w-4 h-4 text-purple-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" clipRule="evenodd" />
            </svg>
            <span>Data Protected</span>
          </div>
        </div>
      </div>

      {/* Modern Footer */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 mt-12">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-1.5 border border-white/30">
                <img 
                  src={YouCRMLogo} 
                  alt="YouCRM" 
                  className="h-5 w-auto"
                />
              </div>
              <span className="text-indigo-100 text-sm">
                Professional form solutions for modern businesses
              </span>
            </div>
            
            {/* Links & Copyright */}
            <div className="flex items-center space-x-6 text-sm text-indigo-200">
              <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
              <span>•</span>
              <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
              <span>•</span>
              <a href="#" className="hover:text-white transition-colors">Support</a>
              <span>•</span>
              <span className="text-indigo-200 text-xs">
                © 2025 YouCRM. All rights reserved.
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicFormFiller;
